import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Message, BMTYPES, ProjectInputSettings } from '@nexxt/common/types';
import emojiRegex from 'emoji-regex';
import styled from 'styled-components';

interface DigitalAvatarProps {
    messageLog: Message[];
    setMessageBubbleBlock: (blockIdx: number) => void;
    inputSettings: ProjectInputSettings;
}

const HEYGEN_API_URL = process.env.HEYGEN_API_URL;
const HEYGEN_API_KEY = process.env.HEYGEN_API_KEY;
const HEYGEN_QUALITY = 'high';
const DELAY_BETWEEN_SYNC_MSGS_MS = 1000;

const EMOJI_REGEX = emojiRegex();
/**
 * https://gist.github.com/tomhicks/95d455186fadda2f7d99e6163aa9d360
 */

const Video = styled.video`
    height: 300px;
    width: 300px;
    margin-left: -80px;
    margin-top: -50px;
    @media (max-width: 768px) {
        height: 250px;
        width: 250px;
    }
`;

function useTaskQueue(params: { shouldProcess: boolean }): {
    tasks: ReadonlyArray<Task>;
    isProcessing: boolean;
    addTask: (task: Task) => void;
} {
    const [queue, setQueue] = React.useState<{
        isProcessing: boolean;
        tasks: Array<Task>;
    }>({ isProcessing: false, tasks: [] });

    React.useEffect(() => {
        if (!params.shouldProcess) return;
        if (queue.tasks.length === 0) return;
        if (queue.isProcessing) return;

        const task = queue.tasks[0];
        setQueue((prev) => ({
            isProcessing: true,
            tasks: prev.tasks.slice(1)
        }));

        Promise.resolve(task()).finally(() => {
            setQueue((prev) => ({
                isProcessing: false,
                tasks: prev.tasks
            }));
        });
    }, [queue, params.shouldProcess]);

    return {
        tasks: queue.tasks,
        isProcessing: queue.isProcessing,
        addTask: React.useCallback((task) => {
            setQueue((prev) => ({
                isProcessing: prev.isProcessing,
                tasks: [...prev.tasks, task]
            }));
        }, [])
    };
}

type Task = () => Promise<void> | void;

const DigitalAvatar: React.FunctionComponent<DigitalAvatarProps> = ({
    messageLog,
    setMessageBubbleBlock,
    inputSettings
}: DigitalAvatarProps) => {
    const [sessionInfo, setSessionInfo] = useState<any>();
    const [peerConnection, setPeerConnection] = useState<any>();
    const [hasInitialized, setHasInitialized] = useState(false);
    const taskQueue = useTaskQueue({ shouldProcess: true });
    const [hasVideoPlayed, setHasVideoPlayed] = useState(false);
    const [hasCompletedMessageSync, setHasCompletedMessageSync] =
        useState(false);
    const videoRef = useRef();

    /**
     * Streams incoming BotMessages into the taskQueue to be processed one-by-one.
     * Since there may be messages in messageLog before the digital avatar is initialized,
     * this hook implements a sync mechanism which adds all of the messages in the log to
     * the queue with a delay of `DELAY_BETWEEN_SYNC_MSGS_MS` milliseconds.
     *
     * This should mostly work well, except that we currently have no way of waiting until
     * the triggered task has completed before triggering the next in the queue, and thus
     * there's a possibility of collision between the various tasks that are being triggered!
     * This can be improved along with other efforts to sync the digital avatar tasks with
     * the message bubbles shown on UI.
     */
    useEffect(() => {
        if (!hasVideoPlayed || !messageLog?.length) return;
        const addTriggerTaskForMsg = (msg: Message, msgIdx: number) => {
            // const latestMsg = messageLog[messageLog.length - 1];
            const msgType = msg?.type;
            const text =
                msgType == BMTYPES.RICH_TEXT
                    ? msg.text.blocks.map((block) => block.text).join('\n')
                    : msgType == BMTYPES.TEXT
                    ? msg.text
                    : '';
            const processedText = text?.trim().replaceAll(EMOJI_REGEX, '');
            if (!processedText?.length) return;
            console.log({ text, msg, processedText, EMOJI_REGEX });
            console.log('adding triggerTask:', processedText);
            taskQueue.addTask(() => triggerTask(processedText, msgIdx));
        };
        if (hasCompletedMessageSync) {
            const msgIdx = messageLog.length - 1;
            const latestMsg = messageLog[msgIdx];
            addTriggerTaskForMsg(latestMsg, msgIdx);
        } else {
            messageLog.forEach(function (msg, index) {
                setTimeout(function () {
                    addTriggerTaskForMsg(msg, index);
                }, index * DELAY_BETWEEN_SYNC_MSGS_MS);
            });
            setHasCompletedMessageSync(true);
        }
    }, [messageLog, hasVideoPlayed]);

    /** Create new digital avatar session once the video element is mounted. */
    useEffect(() => {
        if (!hasInitialized && videoRef.current) {
            setHasInitialized(true);
            createNewSession();
        }
    }, [hasInitialized, videoRef]);

    /** Start and display digital avatar session once it has been created. */
    useEffect(() => {
        if (sessionInfo) {
            startAndDisplaySession();
        }
    }, [sessionInfo]);

    async function handleICE(session_id, candidate) {
        const response = await fetch(`${HEYGEN_API_URL}/v1/streaming.ice`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': HEYGEN_API_KEY
            },
            body: JSON.stringify({ session_id, candidate })
        });
        if (response.status === 500) {
            console.error('Server error');
            throw new Error('Server error');
        } else {
            const data = await response.json();
            return data;
        }
    }

    async function startSession(session_id, sdp) {
        const response = await fetch(`${HEYGEN_API_URL}/v1/streaming.start`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': HEYGEN_API_KEY
            },
            body: JSON.stringify({ session_id, sdp })
        });
        if (response.status === 500) {
            console.error('Server error');
            throw new Error('Server error');
        } else {
            const data = await response.json();
            return data.data;
        }
    }

    async function startAndDisplaySession() {
        // Create and set local SDP description
        const localDescription = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(localDescription);

        // When ICE candidate is available, send to the server
        peerConnection.onicecandidate = ({ candidate }) => {
            console.log('Received ICE candidate:', candidate);
            if (candidate) {
                handleICE(sessionInfo.session_id, candidate.toJSON());
            }
        };

        // When ICE connection state changes, display the new state
        peerConnection.oniceconnectionstatechange = (event) => {
            console.log(
                `ICE connection state changed to: ${peerConnection.iceConnectionState}`
            );
        };

        // Start session
        await startSession(sessionInfo.session_id, localDescription);

        console.log('Session started successfully');
    }

    async function newSession(quality) {
        const response = await fetch(`${HEYGEN_API_URL}/v1/streaming.new`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': HEYGEN_API_KEY
            },
            body: JSON.stringify({
                quality,
                avatar_name:
                    inputSettings?.digitalAvatar?.streamingAvatarId ||
                    'josh_lite3_20230714',
                voice: {
                    voice_id: '433c48a6c8944d89b3b76d2ddcc7176a'
                }
            })
        });
        if (response.status === 500) {
            console.error('Server error');
            throw new Error('Server error');
        } else {
            const data = await response.json();
            console.log(data.data);
            return data.data;
        }
    }

    async function createNewSession() {
        console.log('Creating new session... please wait');

        // call the new interface to get the server's offer SDP and ICE server to create a new RTCPeerConnection
        const newSessionInfo = await newSession(HEYGEN_QUALITY);
        const { sdp: serverSdp, ice_servers2: iceServers } = newSessionInfo;

        // Create a new RTCPeerConnection
        const newPeerConnection = new RTCPeerConnection({
            iceServers: iceServers
        });

        // When audio and video streams are received, display them in the video element
        // const mediaElement = document.querySelector('#mediaElement');
        newPeerConnection.ontrack = (event) => {
            console.log('received the track!');
            if (event.track.kind === 'audio' || event.track.kind === 'video') {
                (videoRef as any).current.srcObject = event.streams[0];
                (videoRef as any).current.onloadedmetadata = function (e) {
                    (videoRef as any).current.play();
                    setHasVideoPlayed(true);
                };
                // setAvatarStream(event.streams[0]);
                // setEventStream(event.streams[0])
                //     (mediaElement as any).srcObject = event.streams[0];
            }
        };

        // When receiving a message, display it in the status element
        newPeerConnection.ondatachannel = (event) => {
            const dataChannel = event.channel;
            console.log('dataChannel.onmessage:', dataChannel.onmessage);
            // dataChannel.onmessage = onMessage;
        };

        // Set server's SDP as remote description
        const remoteDescription = new RTCSessionDescription(serverSdp);
        await newPeerConnection.setRemoteDescription(remoteDescription);
        console.log({ newPeerConnection, newSessionInfo, remoteDescription });
        setPeerConnection(newPeerConnection);
        setSessionInfo(newSessionInfo);
    }

    async function triggerTask(text: string, msgIdx: number) {
        const response = await fetch(`${HEYGEN_API_URL}/v1/streaming.task`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': HEYGEN_API_KEY
            },
            body: JSON.stringify({ session_id: sessionInfo.session_id, text })
        });
        setMessageBubbleBlock(msgIdx);
        if (response.status === 500) {
            console.error('Server error');
            throw new Error('Server error');
        } else {
            const data = await response.json();
            console.log({ data });
            return data.data;
        }
    }

    return (
        <div>
            <Video
                id="mediaElement"
                className="videoEle show"
                autoPlay
                ref={videoRef}
                playsInline
                controls={false}
            />
        </div>
    );
};

export default DigitalAvatar;
